import { useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { exportListAPI } from "../api/api";
import { handleExportResponse } from "../utils/export";
import SuccessMessage from "../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import {
  useStandardizedTracking,
  ExportActionStatus,
  ExportActionContentKeys
} from "../utils/pendo/useStandardizedTracking";

/**
 * Custom hook for handling exports with integrated tracking
 */
export const useExportWithTracking = () => {
  const { t } = useTranslation();
  const { track, EventNames, LayoutTypes, FeatureGroups } = useStandardizedTracking();
  const [isExporting, setIsExporting] = useState(false);

  const formatSortOrder = (sorting) => {
    if (!sorting || !sorting.columnKey || !sorting.order) {
      return { columnKey: null, order: null };
    }
    return {
      columnKey: sorting.columnKey,
      order: sorting.order
    };
  };

  /**
   * Helper function to create tracking metadata as JSON string
   */
  const createTrackingInfo = (trackingMetadata, status, duration) => {
    return JSON.stringify({
      number_records_downloaded: trackingMetadata.recordCount,
      export_type: trackingMetadata.fileType,
      status,
      duration: `${duration}s`,
      sort_order: formatSortOrder(trackingMetadata.sorting),
      columns_visible: trackingMetadata.visibleColumns || [],
      filters_applied: trackingMetadata.filters || {},
      is_saved_list: trackingMetadata.isSavedList || false,
      saved_list_id: trackingMetadata.savedListId || null,
      saved_list_name: trackingMetadata.savedListName || null,
      saved_list_origin_bucket: trackingMetadata.savedListOriginBucket || null
    });
  };

  const exportWithTracking = async (exportParams, trackingMetadata, options = {}) => {
    const { showLoadingMessage = false, loadingMessageKey = "exportMessage", loadingDelay = 500 } = options;

    setIsExporting(true);
    const startTime = Date.now();

    try {
      // Execute export
      const response = await exportListAPI(exportParams);

      // Handle loading message if configured
      if (showLoadingMessage) {
        await new Promise((resolve) => setTimeout(resolve, loadingDelay));
        message.destroy(loadingMessageKey);
      }

      // Download file
      handleExportResponse(response);

      // Show success message
      SuccessMessage(t("export.downloadStarted"));

      // Track successful export
      const duration = (Date.now() - startTime) / 1000;
      try {
        track(EventNames.BUTTON_CLICK, {
          featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
          feature: FeatureGroups.EXPORT_ACTION,
          pageKey: trackingMetadata.pageKey,
          contentType: trackingMetadata.contentType,
          contentKey: ExportActionContentKeys.COMPLETED,
          layout: LayoutTypes.TABLE,
          additionalInfo: createTrackingInfo(trackingMetadata, ExportActionStatus.SUCCESS, duration)
        });
      } catch (trackingError) {
        console.error("Export tracking error:", trackingError);
      }

      return { success: true, response };
    } catch (error) {
      // Handle loading message if configured
      if (showLoadingMessage) {
        message.destroy(loadingMessageKey);
      }

      // Show error message
      ErrorMessage(t("export.error_message"));

      // Track failed export
      const duration = (Date.now() - startTime) / 1000;
      try {
        track(EventNames.BUTTON_CLICK, {
          featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
          feature: FeatureGroups.EXPORT_ACTION,
          pageKey: trackingMetadata.pageKey,
          contentType: trackingMetadata.contentType,
          contentKey: ExportActionContentKeys.FAILURE,
          layout: LayoutTypes.TABLE,
          additionalInfo: createTrackingInfo(trackingMetadata, ExportActionStatus.FAILURE, duration)
        });
      } catch (trackingError) {
        console.error("Export tracking error:", trackingError);
      }

      return { success: false, error };
    } finally {
      setIsExporting(false);
    }
  };

  return { exportWithTracking, isExporting };
};
